import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import SuperAdminLayout from '../components/SuperAdminLayout';

const PhonePeTspAdmin = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [config, setConfig] = useState(null);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchConfig();
    fetchWebhookConfig();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/phonepe-tsp/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchWebhookConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/phonepe-tsp/fetch-webhook`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhookConfig(response.data.data);
      if (response.data.data?.webhookUrl) {
        setWebhookUrl(response.data.data.webhookUrl);
      }
    } catch (error) {
      console.error('Error fetching webhook config:', error);
      setWebhookConfig(null);
    }
  };

  const handleConfigureWebhook = async () => {
    if (!webhookUrl) {
      showMessage('error', 'Please enter a webhook URL');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/phonepe-tsp/configure-webhook`,
        { webhookUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage('success', 'Webhook configured successfully!');
      fetchWebhookConfig();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to configure webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWebhook = async () => {
    if (!webhookUrl) {
      showMessage('error', 'Please enter a webhook URL');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/phonepe-tsp/update-webhook`,
        { 
          webhookUrl,
          events: ['checkout.order.completed', 'checkout.order.failed']
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage('success', 'Webhook updated successfully!');
      fetchWebhookConfig();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to update webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWebhook = async () => {
    if (!window.confirm('Are you sure you want to delete the webhook configuration?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/phonepe-tsp/delete-webhook`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'Webhook deleted successfully!');
      setWebhookConfig(null);
      setWebhookUrl('');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to delete webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleTestWebhook = async () => {
    showMessage('info', 'Testing webhook... Make a test payment to verify.');
  };

  return (
    <SuperAdminLayout>
      <header style={{
        background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
        color: 'white',
        padding: '16px 20px',
        boxShadow: '0 4px 15px rgba(6, 182, 212, 0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 4px 0' }}>📱 PhonePe TSP Integration</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.85rem' }}>Manage payment gateway configuration and webhooks</p>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: '600'
          }}>
            {config?.config?.environment?.toUpperCase() || 'Loading...'}
          </div>
        </div>
      </header>

      <main style={{ padding: '16px', overflow: 'auto' }}>
        <div className="max-w-6xl mx-auto">

        {/* Message Alert */}
        {message.text && (
          <div style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '12px',
            background: message.type === 'success' ? '#dcfce7' : message.type === 'error' ? '#fee2e2' : '#dbeafe',
            color: message.type === 'success' ? '#166534' : message.type === 'error' ? '#991b1b' : '#1e40af',
            border: `1px solid ${message.type === 'success' ? '#86efac' : message.type === 'error' ? '#fca5a5' : '#93c5fd'}`,
            boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)',
          border: '1px solid rgba(6, 182, 212, 0.1)',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
            <button
              onClick={() => setActiveTab('config')}
              style={{
                padding: '12px 20px',
                fontWeight: '600',
                fontSize: '0.9rem',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: activeTab === 'config' ? '#06b6d4' : '#64748b',
                borderBottom: activeTab === 'config' ? '2px solid #06b6d4' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              ⚙️ Configuration
            </button>
            <button
              onClick={() => setActiveTab('webhook')}
              style={{
                padding: '12px 20px',
                fontWeight: '600',
                fontSize: '0.9rem',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: activeTab === 'webhook' ? '#06b6d4' : '#64748b',
                borderBottom: activeTab === 'webhook' ? '2px solid #06b6d4' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              🔗 Webhook Management
            </button>
            <button
              onClick={() => setActiveTab('status')}
              style={{
                padding: '12px 20px',
                fontWeight: '600',
                fontSize: '0.9rem',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: activeTab === 'status' ? '#06b6d4' : '#64748b',
                borderBottom: activeTab === 'status' ? '2px solid #06b6d4' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              📊 Status & Health
            </button>
          </div>

          {/* Configuration Tab */}
          {activeTab === 'config' && (
            <div style={{ padding: '16px' }}>
              <h2 style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '16px', color: '#1e293b' }}>PhonePe Configuration</h2>
              
              {config ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Environment */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(6, 182, 212, 0.2)',
                    boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)'
                  }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '12px', color: '#0891b2' }}>Environment</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label className="text-sm text-gray-600">Current Environment</label>
                        <div className="mt-1">
                          <span className={`px-4 py-2 rounded-full font-medium ${
                            config.config.environment === 'production'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {config.config.environment.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Merchant ID</label>
                        <div className="mt-1 font-mono text-sm bg-white px-3 py-2 rounded border">
                          {config.config.merchantId || 'Dynamic (Production)'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* API Endpoints */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                    <h3 className="text-lg font-semibold mb-4 text-blue-900">API Endpoints</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-gray-600">Authentication URL</label>
                        <div className="mt-1 font-mono text-xs bg-white px-3 py-2 rounded border break-all">
                          {config.config.authUrl}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Checkout URL</label>
                        <div className="mt-1 font-mono text-xs bg-white px-3 py-2 rounded border break-all">
                          {config.config.checkoutUrl}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Status URL</label>
                        <div className="mt-1 font-mono text-xs bg-white px-3 py-2 rounded border break-all">
                          {config.config.statusUrl}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Webhook URL</label>
                        <div className="mt-1 font-mono text-xs bg-white px-3 py-2 rounded border break-all">
                          {config.config.webhookUrl}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Token Status */}
                  <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-lg border border-green-200">
                    <h3 className="text-lg font-semibold mb-4 text-green-900">OAuth Token Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm text-gray-600">Token Status</label>
                        <div className="mt-1">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            config.tokenStatus.isValid
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {config.tokenStatus.isValid ? '✓ Valid' : '✗ Invalid'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Expires At</label>
                        <div className="mt-1 text-sm">
                          {config.tokenStatus.expiresAt 
                            ? new Date(config.tokenStatus.expiresAt).toLocaleString()
                            : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Time Remaining</label>
                        <div className="mt-1 text-sm font-medium">
                          {config.tokenStatus.expiresAt
                            ? Math.max(0, Math.floor((new Date(config.tokenStatus.expiresAt) - new Date()) / 1000 / 60)) + ' minutes'
                            : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* TSP Info */}
                  <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-lg border border-orange-200">
                    <h3 className="text-lg font-semibold mb-4 text-orange-900">TSP Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-600">TSP Name</label>
                        <div className="mt-1 font-mono text-sm bg-white px-3 py-2 rounded border">
                          {config.config.tspName}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Client ID</label>
                        <div className="mt-1 font-mono text-xs bg-white px-3 py-2 rounded border">
                          {config.config.credentials.clientId.substring(0, 20)}...
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">⚙️</div>
                  <p className="text-gray-600">Loading configuration...</p>
                </div>
              )}
            </div>
          )}

          {/* Webhook Management Tab */}
          {activeTab === 'webhook' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">Webhook Management</h2>

              {/* Current Webhook Status */}
              {webhookConfig ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-green-900 mb-2">
                        ✓ Webhook Configured
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <label className="text-sm text-gray-600">Webhook URL</label>
                          <div className="mt-1 font-mono text-sm bg-white px-3 py-2 rounded border break-all">
                            {webhookConfig.webhookUrl}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Subscribed Events</label>
                          <div className="mt-1 flex flex-wrap gap-2">
                            {webhookConfig.events?.map(event => (
                              <span key={event} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                {event}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleDeleteWebhook}
                      disabled={loading}
                      className="ml-4 bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                    ⚠️ No Webhook Configured
                  </h3>
                  <p className="text-yellow-800 text-sm">
                    Configure a webhook to receive real-time payment notifications from PhonePe.
                  </p>
                </div>
              )}

              {/* Configure/Update Webhook Form */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {webhookConfig ? 'Update Webhook' : 'Configure Webhook'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Webhook URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://yourdomain.com/api/phonepe-tsp/webhook"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      This URL must be publicly accessible for PhonePe to send webhook notifications.
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">📋 Events Subscribed:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• checkout.order.completed - Payment successful</li>
                      <li>• checkout.order.failed - Payment failed</li>
                    </ul>
                  </div>

                  <div className="flex gap-3">
                    {webhookConfig ? (
                      <button
                        onClick={handleUpdateWebhook}
                        disabled={loading}
                        style={{
                          background: loading ? '#94a3b8' : '#06b6d4',
                          color: 'white',
                          padding: '8px 20px',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          border: 'none',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)'
                        }}
                        onMouseOver={(e) => !loading && (e.target.style.background = '#0891b2')}
                        onMouseOut={(e) => !loading && (e.target.style.background = '#06b6d4')}
                      >
                        {loading ? 'Updating...' : '🔄 Update Webhook'}
                      </button>
                    ) : (
                      <button
                        onClick={handleConfigureWebhook}
                        disabled={loading}
                        style={{
                          background: loading ? '#94a3b8' : '#06b6d4',
                          color: 'white',
                          padding: '8px 20px',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          border: 'none',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)'
                        }}
                        onMouseOver={(e) => !loading && (e.target.style.background = '#0891b2')}
                        onMouseOut={(e) => !loading && (e.target.style.background = '#06b6d4')}
                      >
                        {loading ? 'Configuring...' : '✓ Configure Webhook'}
                      </button>
                    )}
                    <button
                      onClick={handleTestWebhook}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      🧪 Test Webhook
                    </button>
                  </div>
                </div>
              </div>

              {/* Webhook Documentation */}
              <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">📚 Webhook Documentation</h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <p>
                    <strong>Endpoint:</strong> Your webhook URL will receive POST requests from PhonePe
                  </p>
                  <p>
                    <strong>Authentication:</strong> PhonePe sends an Authorization header with each webhook
                  </p>
                  <p>
                    <strong>Retry Policy:</strong> PhonePe will retry failed webhooks automatically
                  </p>
                  <p>
                    <strong>Response:</strong> Your endpoint should return 200 OK to acknowledge receipt
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status & Health Tab */}
          {activeTab === 'status' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">Status & Health Check</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Integration Status */}
                <div className="bg-gradient-to-br from-green-50 to-teal-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-4">✓ Integration Status</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">OAuth Authentication</span>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        ✓ Active
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Payment API</span>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        ✓ Ready
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Status API</span>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        ✓ Ready
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Webhook</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        webhookConfig
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {webhookConfig ? '✓ Configured' : '⚠ Not Configured'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4">⚡ Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={fetchConfig}
                      className="w-full bg-white hover:bg-gray-50 text-gray-700 px-4 py-3 rounded-lg border border-gray-300 transition-colors text-left"
                    >
                      🔄 Refresh Configuration
                    </button>
                    <button
                      onClick={fetchWebhookConfig}
                      className="w-full bg-white hover:bg-gray-50 text-gray-700 px-4 py-3 rounded-lg border border-gray-300 transition-colors text-left"
                    >
                      🔗 Check Webhook Status
                    </button>
                    <button
                      onClick={() => window.open('/api/phonepe-tsp/status/test', '_blank')}
                      className="w-full bg-white hover:bg-gray-50 text-gray-700 px-4 py-3 rounded-lg border border-gray-300 transition-colors text-left"
                    >
                      🧪 Test Payment Flow
                    </button>
                  </div>
                </div>
              </div>

              {/* System Info */}
              <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">📊 System Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <label className="text-gray-600">Integration Type</label>
                    <div className="mt-1 font-medium">TSP (Third-party Service Provider)</div>
                  </div>
                  <div>
                    <label className="text-gray-600">API Version</label>
                    <div className="mt-1 font-medium">V2 Standard Checkout</div>
                  </div>
                  <div>
                    <label className="text-gray-600">Polling Interval</label>
                    <div className="mt-1 font-medium">30 seconds (fallback)</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </main>
    </SuperAdminLayout>
  );
};

export default PhonePeTspAdmin;
